import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/* ──────────────────────────────────────────────────────────────────────────
   The control bar we draw ourselves, shared by every player on a lesson.

   Two very different things need the same bar:

     • a YouTube embed, driven over postMessage (its own chrome is buried, so
       without this bar it would have no controls at all), and
     • a real <video> — an uploaded file or an HLS stream — whose native controls
       look nothing like the rest of the app and differ per browser.

   They differ only in HOW they are driven, so the bar takes a `transport`: the
   handful of readings and commands every player can supply. Everything the
   viewer actually touches — the scrub rail, the clock, mute, playback speed,
   fullscreen, the idle auto-hide, the keyboard shortcuts — lives here once.
   That is the point: a fix or a new control (the quality menu comes next) is
   written once and every lesson gets it.

   transport = {
     playing, time, duration, muted, rate, rates,
     play(), pause(), seek(seconds), toggleMute(), setRate(rate)
   }
   ────────────────────────────────────────────────────────────────────────── */

/** Standard speed ladder. A player may report a narrower set of its own. */
export const RATES = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];

const RATE_KEY = "ff_video_rate";

/** The member's last chosen speed, re-applied to every later lesson. */
export const readSavedRate = () => {
  try {
    const v = parseFloat(window.localStorage.getItem(RATE_KEY) || "");
    return Number.isFinite(v) && v > 0 ? v : 1;
  } catch { return 1; }
};

export const saveRate = (r) => {
  try { window.localStorage.setItem(RATE_KEY, String(r)); } catch { /* private mode */ }
};

/** "1x" · "1.5x" — trailing zeros trimmed so the button stays short. */
export const fmtRate = (r) => `${String(Number(r)).replace(/\.0+$/, "")}x`;

/** "4:07" · "1:02:11" */
export const fmtClock = (s) => {
  const t = Math.max(0, Math.floor(Number(s) || 0));
  const m = Math.floor(t / 60);
  const h = Math.floor(m / 60);
  const two = (n) => String(n).padStart(2, "0");
  return h > 0 ? `${h}:${two(m % 60)}:${two(t % 60)}` : `${m}:${two(t % 60)}`;
};

/* ---------- transport for a real <video> ---------- */

/**
 * Drive a native <video> through the shared bar.
 *
 * Returns the transport plus `attach`, a ref callback for the <video>. The
 * element arrives as component STATE rather than a ref because listeners have to
 * be re-bound when it mounts, and a ref assignment does not re-run an effect.
 * `externalRef` keeps the page's own videoRef working — note timestamps, resume
 * and progress tracking all read currentTime off it.
 */
export function useNativeTransport(externalRef) {
  const [el, setEl] = useState(null);
  const [s, setS] = useState({ playing: false, time: 0, duration: 0, muted: false, rate: 1 });

  const attach = useCallback((node) => {
    setEl(node);
    if (externalRef) externalRef.current = node;
  }, [externalRef]);

  useEffect(() => {
    if (!el) return undefined;
    const sync = () => setS({
      playing: !el.paused && !el.ended,
      time: el.currentTime || 0,
      duration: Number.isFinite(el.duration) && el.duration > 0 ? el.duration : 0,
      muted: !!el.muted,
      rate: el.playbackRate || 1,
    });
    sync();
    const events = [
      "play", "pause", "ended", "timeupdate", "durationchange",
      "volumechange", "ratechange", "loadedmetadata", "seeked", "emptied",
    ];
    events.forEach((e) => el.addEventListener(e, sync));
    return () => events.forEach((e) => el.removeEventListener(e, sync));
  }, [el]);

  const transport = useMemo(() => ({
    ...s,
    rates: RATES,
    play: () => { try { el?.play?.()?.catch?.(() => {}); } catch { /* ignore */ } },
    pause: () => { try { el?.pause?.(); } catch { /* ignore */ } },
    seek: (t) => {
      if (!el) return;
      const max = Number.isFinite(el.duration) && el.duration > 0 ? el.duration : null;
      const to = Math.max(0, max ? Math.min(t, max) : t);
      try { el.currentTime = to; } catch { /* ignore */ }
    },
    toggleMute: () => { if (el) el.muted = !el.muted; },
    setRate: (r) => { if (el) { try { el.playbackRate = r; } catch { /* ignore */ } } },
  }), [el, s]);

  return { transport, attach };
}

/* ---------- the chrome ---------- */

const IDLE_MS = 3000;

/**
 * @param {object}  transport   see the header
 * @param {string}  primary     brand colour for the played portion + scrub knob
 * @param {string}  mediaKey    changes when the lesson changes; resets per-video state
 * @param {node}    children    the media element itself (<iframe> / <video>)
 * @param {node}    extraMenu   an extra control placed left of the speed button
 * @param {boolean} keepOpen    hold the bar open (an extraMenu is showing)
 * @param {string}  className   extra classes for the frame
 */
export default function VideoChrome({
  transport,
  primary = "#22c55e",
  mediaKey = "",
  children,
  extraMenu = null,
  keepOpen = false,
  className = "",
}) {
  const { playing, time, duration, muted, rate, rates = RATES } = transport;

  const wrapRef = useRef(null);
  const railRef = useRef(null);
  const [scrub, setScrub] = useState(null); // local position while dragging
  const [fs, setFs] = useState(false);
  const [boxFs, setBoxFs] = useState(false); // CSS fallback when the API is unavailable

  const pos = scrub != null ? scrub : time;
  const pct = duration > 0 ? Math.min(100, (pos / duration) * 100) : 0;

  const togglePlay = () => (playing ? transport.pause() : transport.play());
  const seekTo = (secs) => transport.seek(Math.max(0, secs));

  /* ---- speed ---- */
  const [rateOpen, setRateOpen] = useState(false);
  /* How tall the menu may be. The frame clips its own overflow, so on a phone —
     where a 16:9 lesson is only ~220px tall — an eight-row menu was cut off at
     the top edge and "Normal" could not be reached. Measured on open so it is
     right at every size, fullscreen included, and scrolls when it must. */
  const [menuMax, setMenuMax] = useState(240);
  const openRateMenu = () => {
    const h = wrapRef.current?.clientHeight || 0;
    // Leave room for the bar itself plus a little breathing space.
    setMenuMax(Math.max(120, h - 72));
    setRateOpen((o) => !o);
  };
  const [picked, setPicked] = useState(null); // shown until the player confirms
  const shownRate = picked ?? rate ?? 1;
  const rateApplied = useRef(false);

  useEffect(() => { if (picked != null && rate === picked) setPicked(null); }, [rate, picked]);
  useEffect(() => { rateApplied.current = false; setPicked(null); }, [mediaKey]);

  // Re-apply the remembered speed once this video can accept the command —
  // sent any earlier, YouTube drops it and a <video> has no rate to set yet.
  useEffect(() => {
    if (rateApplied.current) return;
    if (!(duration > 0 || playing)) return;
    rateApplied.current = true;
    const saved = readSavedRate();
    if (saved !== 1) { transport.setRate(saved); setPicked(saved); }
  }, [duration, playing, transport]);

  const pickRate = (r) => {
    transport.setRate(r);
    saveRate(r);
    setPicked(r);
    setRateOpen(false);
  };

  useEffect(() => {
    if (!rateOpen) return undefined;
    const close = (e) => { if (!e.target.closest?.("[data-rate-menu]")) setRateOpen(false); };
    const onEsc = (e) => { if (e.key === "Escape") setRateOpen(false); };
    document.addEventListener("pointerdown", close, true);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("pointerdown", close, true);
      document.removeEventListener("keydown", onEsc);
    };
  }, [rateOpen]);

  /* ---- idle auto-hide ----
     Hover alone is not enough: a viewer normally leaves the pointer resting ON
     the video, and a hover rule then holds the bar open for the whole lesson.
     Any movement, tap or key wakes it; IDLE_MS of stillness while the video runs
     hides it again, cursor included. It never hides while paused, mid-drag, with
     a menu open, or while something in it holds keyboard focus. */
  const [idle, setIdle] = useState(false);
  const idleTimer = useRef(0);

  const wake = useCallback(() => {
    setIdle(false);
    clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(() => setIdle(true), IDLE_MS);
  }, []);

  useEffect(() => () => clearTimeout(idleTimer.current), []);

  useEffect(() => {
    if (playing) { wake(); return; }
    clearTimeout(idleTimer.current);
    setIdle(false);
  }, [playing, wake]);

  const onPointerLeave = () => {
    clearTimeout(idleTimer.current);
    if (playing) setIdle(true);
  };

  const hidden = playing && idle && scrub == null && !rateOpen && !keepOpen;

  /* ---- fullscreen ----
     Chrome/Firefox use the unprefixed API, Safari the webkit one, and iOS Safari
     has NO element fullscreen at all, so a bare requestFullscreen() silently did
     nothing there — and its promise rejects rather than throwing when the browser
     refuses (e.g. the page is itself framed without allow="fullscreen"). Every
     one of those paths falls back to boxFs: a fixed inset-0 blow-up that needs no
     permission. */
  const fsElement = () => document.fullscreenElement || document.webkitFullscreenElement || null;

  useEffect(() => {
    const onFs = () => {
      const active = fsElement() === wrapRef.current;
      setFs(active);
      if (active) setBoxFs(false);
    };
    document.addEventListener("fullscreenchange", onFs);
    document.addEventListener("webkitfullscreenchange", onFs);
    return () => {
      document.removeEventListener("fullscreenchange", onFs);
      document.removeEventListener("webkitfullscreenchange", onFs);
    };
  }, []);

  useEffect(() => {
    if (!boxFs) return undefined;
    const onEsc = (e) => { if (e.key === "Escape") setBoxFs(false); };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [boxFs]);

  const expanded = fs || boxFs;

  const toggleFs = () => {
    const el = wrapRef.current;
    if (!el) return;
    if (fsElement()) {
      const exit = document.exitFullscreen || document.webkitExitFullscreen;
      try { exit?.call(document); } catch { /* ignore */ }
      return;
    }
    if (boxFs) { setBoxFs(false); return; }
    const req = el.requestFullscreen || el.webkitRequestFullscreen || el.msRequestFullscreen;
    if (!req) { setBoxFs(true); return; }
    try {
      const p = req.call(el);
      if (p && typeof p.catch === "function") p.catch(() => setBoxFs(true));
    } catch { setBoxFs(true); }
  };

  /* ---- scrubbing: pointer events on the rail, so a drag tracks outside it ---- */
  const posFromEvent = (e) => {
    const r = railRef.current?.getBoundingClientRect();
    if (!r || !duration) return 0;
    return ((e.clientX - r.left) / r.width) * duration;
  };

  const onRailDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setScrub(posFromEvent(e));
    const move = (ev) => setScrub(posFromEvent(ev));
    const up = (ev) => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      seekTo(posFromEvent(ev));
      setScrub(null);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  const onKey = (e) => {
    wake();
    if (e.key === " " || e.key === "k") { e.preventDefault(); togglePlay(); }
    else if (e.key === "ArrowRight") { e.preventDefault(); seekTo(pos + 5); }
    else if (e.key === "ArrowLeft") { e.preventDefault(); seekTo(pos - 5); }
    else if (e.key === "f") { e.preventDefault(); toggleFs(); }
    else if (e.key === "Escape" && boxFs) { e.preventDefault(); setBoxFs(false); }
    else if (e.key === "m") { e.preventDefault(); transport.toggleMute(); }
  };

  const btn = "flex h-8 w-8 items-center justify-center rounded-full text-white/90 transition hover:bg-white/15 hover:text-white";

  return (
    <div
      ref={wrapRef}
      className={`group overflow-hidden bg-black focus:outline-none ${
        boxFs ? "fixed inset-0 z-[9999] h-[100dvh] w-screen" : "relative aspect-video"
      } ${hidden ? "cursor-none" : ""} ${className}`}
      tabIndex={0}
      onKeyDown={onKey}
      onPointerMove={wake}
      onPointerDown={wake}
      onPointerLeave={onPointerLeave}
    >
      {children}

      {/* Click layer: play/pause. On a YouTube embed it also keeps YouTube's own
          in-video surfaces (end-screen cards, big play button) from taking the
          click. */}
      <div
        className="absolute inset-0 z-10 cursor-pointer"
        onClick={togglePlay}
        /* Double-click for fullscreen: native <video> controls give viewers this
           habit, and replacing those controls should not take it away. */
        onDoubleClick={toggleFs}
        aria-hidden="true"
      />

      {/* The bar — up while paused, while the pointer moves, and whenever
          anything inside it holds focus; gone after IDLE_MS of stillness.
          pointer-events-none while hidden so an invisible bar can never eat a
          click meant for the video. */}
      <div className={`absolute inset-x-0 bottom-0 z-20 bg-gradient-to-t from-black/85 via-black/45 to-transparent px-3 pb-2 pt-6 transition-opacity duration-200 group-focus-within:pointer-events-auto group-focus-within:opacity-100 ${hidden ? "pointer-events-none opacity-0" : "opacity-100"}`}>
        <div
          ref={railRef}
          onPointerDown={onRailDown}
          className="group/rail relative mb-1.5 h-3 cursor-pointer"
          role="slider"
          aria-label="Seek"
          aria-valuemin={0}
          aria-valuemax={Math.round(duration) || 0}
          aria-valuenow={Math.round(pos) || 0}
          tabIndex={-1}
        >
          <div className="absolute inset-x-0 top-1/2 h-1 -translate-y-1/2 rounded-full bg-white/30">
            <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: primary }} />
          </div>
          <div
            className="absolute top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full opacity-0 transition-opacity group-hover/rail:opacity-100"
            style={{ left: `${pct}%`, backgroundColor: primary }}
          />
        </div>
        <div className="flex items-center gap-1.5 text-[11px] font-semibold text-white/90">
          <button type="button" className={btn} onClick={togglePlay} aria-label={playing ? "Pause" : "Play"}>
            {playing ? (
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor"><path d="M6 5h4v14H6zM14 5h4v14h-4z" /></svg>
            ) : (
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
            )}
          </button>
          <button type="button" className={btn} onClick={transport.toggleMute} aria-label={muted ? "Unmute" : "Mute"}>
            {muted ? (
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor"><path d="M16.5 12A4.5 4.5 0 0014 7.97v2.21l2.45 2.45c.03-.2.05-.41.05-.63zM3 4.27l2.99 2.99L4 7.5v9h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06a8.9 8.9 0 003.69-1.81L19.73 21 21 19.73 4.27 3 3 4.27zM13 3l-2.1 2.1L13 7.2V3z" /></svg>
            ) : (
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor"><path d="M4 7.5h4l5-5v19l-5-5H4v-9zm12.5 4.5a4.5 4.5 0 00-2.5-4.03v8.06A4.5 4.5 0 0016.5 12zM14 2.23v2.06a8 8 0 010 15.42v2.06a10 10 0 000-19.54z" /></svg>
            )}
          </button>
          <span className="ml-0.5 tabular-nums">{fmtClock(pos)} <span className="text-white/50">/ {fmtClock(duration)}</span></span>

          {/* Whatever the player adds of its own — the HLS quality menu. */}
          <div className="ml-auto flex items-center gap-1.5">
            {extraMenu}

            {/* Playback speed. On a YouTube embed this is the only way to reach
                it at all: YouTube's own speed control lives in the chrome we
                bury. The menu opens upward — the bar sits on the bottom edge. */}
            <div className="relative" data-rate-menu>
              <button
                type="button"
                onClick={openRateMenu}
                className={`flex h-8 min-w-8 items-center justify-center rounded-full px-2 text-[11px] font-bold tabular-nums transition ${
                  shownRate !== 1 ? "bg-white/20 text-white" : "text-white/90 hover:bg-white/15 hover:text-white"
                }`}
                aria-haspopup="menu"
                aria-expanded={rateOpen}
                aria-label={`Playback speed, currently ${fmtRate(shownRate)}`}
                title="Playback speed"
              >
                {fmtRate(shownRate)}
              </button>
              {rateOpen ? (
                <div
                  role="menu"
                  style={{ maxHeight: menuMax }}
                  className="absolute bottom-full right-0 z-30 mb-2 min-w-[6.5rem] overflow-y-auto overscroll-contain rounded-xl border border-white/10 bg-gradient-to-b from-[#1c222f] to-[#0c1018] p-1 shadow-[0_22px_46px_-16px_rgba(0,0,0,0.8)] backdrop-blur-md"
                >
                  <div className="sticky top-0 bg-[#1c222f] px-2 pb-1 pt-1 text-[9px] font-extrabold uppercase tracking-[0.09em] text-white/45">
                    Speed
                  </div>
                  {rates.map((r) => (
                    <button
                      key={r}
                      type="button"
                      role="menuitemradio"
                      aria-checked={r === shownRate}
                      onClick={() => pickRate(r)}
                      className={`flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-[12px] font-semibold transition ${
                        r === shownRate ? "bg-white/15 text-white" : "text-white/75 hover:bg-white/10 hover:text-white"
                      }`}
                    >
                      <span className="w-3 text-center text-[10px]">{r === shownRate ? "✓" : ""}</span>
                      {r === 1 ? "Normal" : fmtRate(r)}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>

            <button type="button" className={btn} onClick={toggleFs} aria-label={expanded ? "Exit full screen" : "Full screen"}>
              {expanded ? (
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor"><path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z" /></svg>
              ) : (
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor"><path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z" /></svg>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
